"use client"

import { Avatar, Button, Text, TextInput } from "@/elements"
import { TextArea } from "@/elements/TextArea"
import { FragmentType, graphql, useFragment } from "@/gql"
import {
  StartClientToCreativeNetworkingChatDocument,
  StartCreativeNetworkingChatDocument,
  StartJobApplicationChatDocument,
} from "@/gql/graphql"
import { useSession } from "@/hooks/useSession"
import { useMutation } from "@apollo/client"
import React, { FC, useState } from "react"
import { useForm } from "react-hook-form"
import { toast } from "@/components/Toast"
import { ampli } from "@/ampli"
import { decodeGraphqlNodeId } from "@/tracking"

const MessageModalInitialMessageFragment = graphql(`
  fragment MessageModalInitialMessage on User {
    id
    uuid
    firstName
    avatarInitials
    profile {
      id
      avatar240w: avatarUrl(width: 240)
    }
    creative {
      id
      displayName
    }
  }
`)

interface FormValues {
  message: string
}

interface Props {
  closeModal: () => void
  trackingEntryPoint: string
  user: FragmentType<typeof MessageModalInitialMessageFragment> | undefined | null
  applicationId?: string
  jobId?: string
}

export const MessageModalInitialMessage: FC<Props> = props => {
  const { closeModal, applicationId, trackingEntryPoint, jobId } = props
  const session = useSession()
  const user = useFragment(MessageModalInitialMessageFragment, props.user)
  const creativeId = user?.creative?.id!

  const [loading, setLoading] = useState(false)
  const [startCreativeChat] = useMutation(StartCreativeNetworkingChatDocument)
  const [startClientChat] = useMutation(StartClientToCreativeNetworkingChatDocument)
  const [startJobApplicationChat] = useMutation(StartJobApplicationChatDocument)
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      message: "",
    },
  })

  const sessionUserIsClient = session?.data?.account === "client"

  const onSubmit = async (formData: FormValues) => {
    if (loading) return
    setLoading(true)
    try {
      let chatId = null
      if (applicationId) {
        const { data } = await startJobApplicationChat({
          variables: { input: { jobApplication: applicationId, initialMessage: formData.message } },
        })
        if (data?.startJobApplicationChat?.jobApplicationChat?.streamChannelId) {
          reset()
          closeModal()
          chatId = data?.startJobApplicationChat?.jobApplicationChat?.id
          toast.success("Your message was sent")
        } else {
          toast.error("There was a problem sending your message")
        }

        if (jobId && chatId) {
          ampli.jobChatStart({
            chat_id: decodeGraphqlNodeId(chatId).numericalId || "",
            job_id: decodeGraphqlNodeId(jobId).numericalId || "",
            creative_id: decodeGraphqlNodeId(creativeId).numericalId || "",
            account_type: session?.data?.account || "",
          })
        }
      } else {
        const { data } = await (sessionUserIsClient
          ? startClientChat({ variables: { creativeId, message: formData.message } })
          : startCreativeChat({ variables: { creativeId, message: formData.message } }))

        if (data?.startNetworkingChat?.ok) {
          reset()
          closeModal()
          chatId = data?.startNetworkingChat
          toast.success("Your message was sent")
        } else {
          toast.error("There was a problem sending your message")
        }
      }
    } catch (error) {
      toast.error("There was a problem sending your message")
    }

    setLoading(false)
  }

  const textDisplay = `Send ${user?.creative?.displayName || user?.firstName} a message`

  const onClickSend = async () => {
    await handleSubmit(onSubmit)()
  }

  return (
    <div className="flex flex-1 flex-col">
      <div className="mt-[150px] flex flex-1 flex-col items-center">
        <Avatar src={user?.profile?.avatar240w} initials={user?.avatarInitials} size="2xl" />
        <Text variant="h5" className="mt-4">
          {textDisplay}
        </Text>
      </div>

      <div className="flex w-full flex-col bg-bg-2 px-5 py-4">
        <TextArea
          {...register("message", {
            required: "A message is required",
          })}
          placeholder="Your message..."
          className="h-[120px] w-full rounded-xl bg-bg-1 px-3 py-2"
        />

        <Button type="submit" className="mt-4 w-fit self-end" loading={loading} onClick={onClickSend}>
          Send
        </Button>
      </div>
    </div>
  )
}
